import React, { useEffect, useState } from 'react';
import {
  Box, Typography, CircularProgress, TextField, Button,
} from '@mui/material';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import {
  addCadParcelVideo,
  getCadParcelVideos,
  deleteCadParcelVideo,
} from '../../services/cadService';
import { toast } from '../../Components/Toast';
import { confirmDialog } from '../../Components/ConfirmDialog';
import { colors, typography } from '../../styles/theme';
import { FORM_SECONDARY_BUTTON_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';
import ListingVideoEmbeds from '../../Map/components/DockPanel/Tabs/ListingVideoEmbeds';
import {
  UF_MAX_VIDEOS,
  UF_VIDEO_LIMIT_TOAST,
  UF_VIDEO_INVALID_TOAST,
  extractYoutubeVideoId,
  normalizeListingVideosFromPayload,
  resolveVideoApiMessage,
  isVideoLimitError,
  isVideoDuplicateError,
} from '../../Map/utils/listingVideoUtils';

const CAD_PARCEL_VIDEO_DUPLICATE_TOAST = {
  title: 'Video duplicado',
  message: 'Ese video ya está agregado en este lote.',
};

const applyVideos = (payload) => normalizeListingVideosFromPayload(payload);

/**
 * Sección Videos de una parcela CAD: agregar URL de YouTube, listar embeds
 * y borrar. Separada de CadParcelImages (fotos) y de UF / lot-content.
 * Tope: UF_MAX_VIDEOS (4). Sin título. Shorts OK.
 */
const CadParcelVideos = ({ parcelId, canEdit, initialVideos, onVideosChange }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [urlInput, setUrlInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());

  const notifyChange = (next) => {
    const normalized = applyVideos(next);
    setVideos(normalized);
    onVideosChange?.(normalized);
  };

  useEffect(() => {
    if (!parcelId) return;
    if (Array.isArray(initialVideos)) {
      setVideos(applyVideos(initialVideos));
      setLoading(false);
      return;
    }
    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcelId, initialVideos]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const res = await getCadParcelVideos(parcelId);
      if (res._httpOk) {
        notifyChange(res.data ?? res);
      }
    } catch (e) {
      console.error('Error fetching CAD parcel videos:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    const youtubeUrl = urlInput.trim();
    if (!youtubeUrl) {
      toast.warning(UF_VIDEO_INVALID_TOAST);
      return;
    }
    if (videos.length >= UF_MAX_VIDEOS) {
      toast.warning(UF_VIDEO_LIMIT_TOAST);
      return;
    }
    if (!extractYoutubeVideoId(youtubeUrl)) {
      toast.warning(UF_VIDEO_INVALID_TOAST);
      return;
    }

    setAdding(true);
    try {
      const res = await addCadParcelVideo(parcelId, youtubeUrl);
      if (!res._httpOk || !res.data) {
        const msg = resolveVideoApiMessage(res);
        if (isVideoLimitError(res._httpStatus, msg)) {
          toast.warning(UF_VIDEO_LIMIT_TOAST);
        } else if (isVideoDuplicateError(res._httpStatus, msg)) {
          toast.warning(CAD_PARCEL_VIDEO_DUPLICATE_TOAST);
        } else {
          toast.error({
            title: 'Error',
            message: msg || 'No se pudo agregar el video.',
          });
        }
        return;
      }
      notifyChange([...videos, res.data]);
      setUrlInput('');
      toast.success('Video agregado.');
    } catch (e) {
      console.error('Error adding CAD parcel video:', e);
      toast.error({ title: 'Error', message: 'No se pudo agregar el video.' });
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (video) => {
    if (!video?.id) return;

    const result = await confirmDialog({
      title: '¿Eliminar video?',
      description: 'Se eliminará de este lote. Esta acción no se puede deshacer.',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!result.isConfirmed) return;

    setDeletingIds((prev) => new Set([...prev, video.id]));
    const prevVideos = videos;
    notifyChange(videos.filter((item) => item.id !== video.id));

    try {
      const res = await deleteCadParcelVideo(video.id);
      if (!res._httpOk) {
        toast.error({
          title: 'Error',
          message: resolveVideoApiMessage(res) || 'No se pudo eliminar el video.',
        });
        throw new Error('delete failed');
      }
      if (Array.isArray(res.data?.videos)) {
        notifyChange(res.data.videos);
      }
      toast.success('Video eliminado con éxito');
    } catch (e) {
      if (e.message !== 'delete failed') {
        console.error('Error deleting CAD parcel video:', e);
        toast.error({ title: 'Error', message: 'No se pudo eliminar el video.' });
      }
      notifyChange(prevVideos);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(video.id);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} sx={{ color: '#ffffff' }} />
      </Box>
    );
  }

  const atLimit = videos.length >= UF_MAX_VIDEOS;
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#242424',
      borderRadius: '8px',
      '& fieldset': { borderColor: '#4A4A4A' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.28)' },
      '&.Mui-focused fieldset': { borderColor: colors.primary },
    },
    '& .MuiInputBase-input': {
      color: '#ffffff',
      fontSize: typography.fontSize.sm,
      fontFamily: typography.fontFamily.primary,
    },
    '& .MuiInputLabel-root': {
      fontSize: typography.fontSize.sm,
      color: 'rgba(255,255,255,0.72)',
      fontFamily: typography.fontFamily.primary,
      '&.Mui-focused': { color: '#ffffff' },
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      {videos.length > 0 ? (
        <ListingVideoEmbeds
          videos={videos}
          canEdit={canEdit}
          deletingIds={deletingIds}
          onDelete={canEdit ? handleDelete : null}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            py: 2.5,
            borderRadius: 1.5,
            border: '1px dashed rgba(255,255,255,0.12)',
            bgcolor: 'rgba(255,255,255,0.02)',
          }}
        >
          <VideocamOutlinedIcon sx={{ fontSize: 22, color: 'rgba(255,255,255,0.28)' }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px' }}>
            Sin videos
          </Typography>
        </Box>
      )}

      {canEdit && !atLimit && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            fullWidth
            label="URL de YouTube"
            placeholder="https://youtu.be/… o /shorts/…"
            value={urlInput}
            disabled={adding}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (!adding) handleAdd();
              }
            }}
            sx={fieldSx}
          />
          <Button
            variant="outlined"
            disabled={adding || !urlInput.trim()}
            onClick={handleAdd}
            sx={{
              ...FORM_SECONDARY_BUTTON_SX,
              flexShrink: 0,
              px: 1.5,
            }}
          >
            Agregar
          </Button>
        </Box>
      )}

      {canEdit && (
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.30)',
            fontSize: '11px',
            textAlign: 'center',
            fontStyle: atLimit ? 'italic' : 'normal',
          }}
        >
          {atLimit
            ? `Límite de ${UF_MAX_VIDEOS} videos alcanzado`
            : `Videos ${videos.length}/${UF_MAX_VIDEOS} · Solo links de YouTube`}
        </Typography>
      )}
    </Box>
  );
};

export default CadParcelVideos;
